import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AppVarsContext } from '../../context/AppVarsContext';
import { persistReportId } from '../report-id-storage';
import { buildReportAccessHeaders } from '../report-access';
import GoLinkSetter from '../../routes/GoLinkSetter';
import ReportAccessPage from '../../routes/ReportAccessPage';

const normalizeSharedRedirect = value => {
    if (!value || typeof value !== 'string') return '';
    try {
        const path = decodeURIComponent(value).trim();
        if (!path.startsWith('/') || path.startsWith('//')) return '';
        if (path.startsWith('/session/') || path.startsWith('/go/')) return '';
        return path;
    } catch { return ''; }
};

export default function SetSessionInfo() {
    const location = useLocation();
    const navigate = useNavigate();
    const id = location.pathname.split('/')[2];
    const { appVars, appLoading } = useContext(AppVarsContext);
    const [failure, setFailure] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt(value => value + 1), []);

    useEffect(() => {
        if (!id || appLoading || !appVars?.api_url) return undefined;
        let active = true;
        setFailure(null);
        const run = async () => {
            try {
                const slug = sessionStorage.getItem('reportSlug') || '';
                const options = { headers: buildReportAccessHeaders(slug) };
                const read = async path => {
                    const response = await fetch(`${appVars.api_url.replace(/\/$/, '')}${path}`, options);
                    const data = await response.json();
                    if (!response.ok) throw Object.assign(new Error('Report setup unavailable'), { data });
                    return data;
                };
                // Verify access before initializing or mounting report providers.
                const profile = await read(`/reportprofile/${encodeURIComponent(id)}`);
                if (!active) return;
                const client = await read(`/tagmanager/${encodeURIComponent(id)}`);
                if (!active) return;
                const settings = typeof profile.settings === 'string' ? JSON.parse(profile.settings) : profile.settings || {};
                const reports = profile.reportNavigationMode === 'assigned' ? [] : await read('/reports/list');
                if (!active) return;
                const found = Array.isArray(reports) ? reports.find(item => item?.pkRecID === settings.default_report) : null;
                const reportSettings = typeof found?.settings === 'string' ? JSON.parse(found.settings) : found?.settings;
                const home = profile.reportNavigationMode === 'assigned' ? '/mr' : normalizeSharedRedirect(reportSettings?.location) || '/mr';
                const query = new URLSearchParams(location.search);
                const redirect = normalizeSharedRedirect(query.get('rd')) || home;
                persistReportId(id);
                sessionStorage.setItem('client', JSON.stringify(client));
                sessionStorage.setItem('apiID', client.client);
                sessionStorage.setItem('aPort', client.aPort);
                sessionStorage.setItem('agencyName', client.clientname);
                sessionStorage.setItem('clientName', client.name);
                for (const [key, param] of [['sDate', 's'], ['eDate', 'e']]) {
                    if (query.get(param)) sessionStorage.setItem(key, query.get(param));
                    else sessionStorage.removeItem(key);
                }
                localStorage.setItem('_home', home);
                navigate(redirect, { replace: true });
            } catch (error) {
                if (active) setFailure(error.data || {});
            }
        };
        run();
        return () => { active = false; };
    }, [id, appLoading, appVars?.api_url, location.search, navigate, attempt]);

    if (failure?.error?.code === 'report_password_required' && failure.slug) {
        return <GoLinkSetter requestedSlug={failure.slug} onAccessGranted={retry} />;
    }
    if (failure) return <ReportAccessPage unavailable onRetry={retry} />;
    return <Box role="status" sx={{ minHeight: '100dvh', display: 'grid', placeContent: 'center', justifyItems: 'center', gap: 2 }}>
        <CircularProgress size={28} /><Typography>Opening your report…</Typography>
    </Box>;
}
